export type ImportResolutionActionType =
  | 'link_view_student'
  | 'link_view_existing_student'
  | 'link_view_family'
  | 'link_add_student'
  | 'export_row'
  | 'manual_only';

export interface ImportResolutionAction {
  type: ImportResolutionActionType;
  label: string;
}

export interface ImportResolutionPlaybookEntry {
  title: string;
  description: string;
  manualSteps: string[];
  actions: ImportResolutionAction[];
}

export const IMPORT_REASON_LABELS: Record<string, string> = {
  shared_parent_email_cleared:
    'Student created — shared parent email was cleared from the student record.',
  near_match_created_with_warning:
    'Student created, but a similar student already exists under this parent.',
  duplicate_name_no_dob:
    'Duplicate name without date of birth — only the first occurrence was kept.',
  duplicate_name_no_dob_existing:
    'A student with this name already exists for this parent (no DOB on file).',
  hard_duplicate:
    'Same name and date of birth already exists — row was skipped.',
  invalid_student_name: 'Missing or invalid student name.',
  near_match:
    'A similar student already exists for this parent — row was skipped for review.',
  student_email_matches_parent:
    'Student email matches the parent email — provide a separate student email.',
  missing_parent_email:
    'Missing Parent Email — row skipped (Parent Email is required to import a family).',
};

export function reasonLabel(reason: string): string {
  return IMPORT_REASON_LABELS[reason] ?? reason.replace(/_/g, ' ');
}

const PLAYBOOK: Record<string, ImportResolutionPlaybookEntry> = {
  shared_parent_email_cleared: {
    title: 'Shared family email',
    description:
      'The student was created under the parent. Parent and student shared an email, so the student email was cleared.',
    manualSteps: [
      'Open the student profile if they need their own login email later.',
      'Add a unique student email under Contacts or Details.',
    ],
    actions: [
      { type: 'link_view_student', label: 'View student' },
      { type: 'link_view_family', label: 'View family' },
    ],
  },
  near_match_created_with_warning: {
    title: 'Similar student exists',
    description:
      'A new student was created, but another student with a similar name and same date of birth already exists for this parent.',
    manualSteps: [
      'Confirm they are different people.',
      'If the new row was a mistake, delete the newly created student from the family list.',
      'If they are twins or nicknames, keep both and update names or DOB on the student profile if needed.',
    ],
    actions: [
      { type: 'link_view_student', label: 'View new student' },
      { type: 'link_view_existing_student', label: 'View similar student' },
      { type: 'link_view_family', label: 'View family' },
    ],
  },
  hard_duplicate: {
    title: 'Duplicate student skipped',
    description:
      'Import skipped because name and date of birth already match an existing student. No new record was created.',
    manualSteps: [
      'If this is the same child, no action is needed.',
      'If this is a different child, correct the CSV (name or DOB) and re-import, or add the student manually from the family page.',
    ],
    actions: [
      { type: 'link_view_existing_student', label: 'View existing student' },
      { type: 'link_view_family', label: 'View family' },
      { type: 'link_add_student', label: 'Add student manually' },
    ],
  },
  duplicate_name_no_dob: {
    title: 'Duplicate name (no DOB)',
    description:
      'Multiple CSV rows for the same parent had the same student name with no date of birth. Only the first was imported.',
    manualSteps: [
      'Add a date of birth to the CSV row(s) and re-import, or',
      'Add remaining students manually from the family page.',
    ],
    actions: [
      { type: 'link_view_family', label: 'View family' },
      { type: 'link_add_student', label: 'Add student manually' },
    ],
  },
  duplicate_name_no_dob_existing: {
    title: 'Name already exists (no DOB)',
    description:
      'A student with this name already exists for the parent and the CSV row had no date of birth.',
    manualSteps: [
      'Add DOB to the CSV and re-import, or',
      'Confirm the existing student record is correct.',
    ],
    actions: [
      { type: 'link_view_family', label: 'View family' },
      { type: 'link_view_existing_student', label: 'View existing student' },
    ],
  },
  invalid_student_name: {
    title: 'Invalid student name',
    description: 'The CSV row is missing a valid first or last name.',
    manualSteps: [
      'Fix first and last name in the CSV and re-import this row, or',
      'Add the student manually from the family page.',
    ],
    actions: [{ type: 'export_row', label: 'Include in issues export' }],
  },
  near_match: {
    title: 'Similar student exists',
    description:
      'A student with a similar name already exists for this parent, so the row was skipped for review instead of creating a possible duplicate.',
    manualSteps: [
      'If it is the same child, no action is needed.',
      'If it is a different child, correct the name or DOB in the CSV and re-import, or add the student manually from the family page.',
    ],
    actions: [
      { type: 'link_view_existing_student', label: 'View existing student' },
      { type: 'link_view_family', label: 'View family' },
      { type: 'link_add_student', label: 'Add student manually' },
    ],
  },
  student_email_matches_parent: {
    title: 'Student email matches parent',
    description:
      'The student email is the same as the parent email, which cannot be used as a separate student login.',
    manualSteps: [
      'Give the student their own unique email in the CSV and re-import, or',
      'Leave the student email blank and add one later from the student profile.',
    ],
    actions: [
      { type: 'link_view_family', label: 'View family' },
      { type: 'export_row', label: 'Include in issues export' },
    ],
  },
  missing_parent_email: {
    title: 'Missing parent email',
    description:
      'The CSV row has no Parent Email, which is required to import a family, so the row was skipped.',
    manualSteps: [
      'Add a Parent Email to this CSV row and re-import it, or',
      'Add the parent and student manually from the parents page.',
    ],
    actions: [{ type: 'export_row', label: 'Include in issues export' }],
  },
};

export function getImportResolutionPlaybook(
  manualResolutionKey: string,
): ImportResolutionPlaybookEntry {
  return (
    PLAYBOOK[manualResolutionKey] ?? {
      title: reasonLabel(manualResolutionKey),
      description: 'Review this row and take action as needed.',
      manualSteps: [
        'Check the parent and student in your account.',
        'Fix the CSV and re-import if needed.',
      ],
      actions: [{ type: 'link_view_family', label: 'View family' }],
    }
  );
}

export interface ImportActionRowContext {
  success: boolean;
  failureReason?: string;
  parentId?: string;
  parentEmail: string;
  parentFirstName?: string;
  parentLastName?: string;
  studentFirstName: string;
  studentLastName: string;
  createdStudentId?: string;
  existingStudentId?: string;
  rowHint?: string;
  reason: string;
  manualResolutionKey: string;
  message?: string;
  kind: 'warning' | 'attention';
}

export function buildActionRowContextFromWarning(
  row: {
    success: true;
    parentEmail: string;
    parentId?: string;
    parentFirstName?: string;
    parentLastName?: string;
    studentFirstName: string;
    studentLastName: string;
    createdStudentId?: string;
    existingStudentId?: string;
    rowHint?: string;
    reason: string;
    manualResolutionKey: string;
    message: string;
  },
): ImportActionRowContext {
  return { ...row, kind: 'warning' };
}

export function buildActionRowContextFromUnresolved(
  row: {
    success: false;
    failureReason: string;
    parentEmail: string;
    parentId?: string;
    parentFirstName?: string;
    parentLastName?: string;
    studentFirstName: string;
    studentLastName: string;
    existingStudentId?: string;
    rowHint?: string;
    reason: string;
    manualResolutionKey: string;
  },
): ImportActionRowContext {
  return {
    ...row,
    kind: 'attention',
    reason: row.failureReason ?? row.reason,
  };
}
